import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import {
  GEMINI_AMBER,
  isMediumPriorityLabel,
  resolvePriorityColor,
} from "../../../assets/theme/geminiPalette";
import { getIncidentPriorityLabel } from "./constants";
import type { Incident } from "../../../types/incident";
import { useTranslation } from "react-i18next";
import { optionLabel } from "../../../lib/i18n/optionLabel";

const ANOMALY_SEVERITY_WEIGHT: Record<string, number> = {
  CRISIS: 6,
  CRITICAL: 5,
  Critical: 5,
  HIGH: 4,
  High: 4,
  MEDIUM: 3,
  Medium: 3,
  LOW: 2,
  Low: 2,
  Informational: 1,
  INFORMATIONAL: 1,
};

function anomalySeverityWeight(value?: string | null): number {
  if (!value) return 0;
  return ANOMALY_SEVERITY_WEIGHT[value] ?? ANOMALY_SEVERITY_WEIGHT[value.toUpperCase()] ?? 0;
}

export const hasDetectedAnomalyEvents = (row: { detectedAnomalyEvents?: unknown }) =>
  Array.isArray(row.detectedAnomalyEvents) && row.detectedAnomalyEvents.length > 0;

export function resolveDetectedAnomalyTone(
  incident: Pick<Incident, "detectedAnomalyEvents">,
): { color: string; severity?: string } | undefined {
  if (!hasDetectedAnomalyEvents(incident)) return undefined;

  const severities = (incident.detectedAnomalyEvents ?? [])
    .map((event) => event.definition?.defaultSeverity?.trim())
    .filter((value): value is string => Boolean(value));

  if (severities.length === 0) {
    return { color: GEMINI_AMBER };
  }

  const severity = [...severities].sort(
    (left, right) => anomalySeverityWeight(right) - anomalySeverityWeight(left),
  )[0];

  return {
    severity,
    color: resolvePriorityColor(severity),
  };
}

const anomalyAlarmPulseSx = {
  "@keyframes anomalyAlarmPulse": {
    "0%, 100%": { opacity: 1, filter: "drop-shadow(0 0 2px currentColor)" },
    "50%": { opacity: 0.3, filter: "drop-shadow(0 0 8px currentColor)" },
  },
} as const;

const renderAnomalyAlarmIcon = () => (
  <Box
    component="span"
    sx={{
      ...anomalyAlarmPulseSx,
      display: "inline-flex",
      alignItems: "center",
      color: "inherit",
      animation: "anomalyAlarmPulse 1.1s ease-in-out infinite",
      mr: -0.25,
    }}
    aria-hidden
  >
    <NotificationsActiveIcon sx={{ fontSize: 18 }} />
  </Box>
);

export const statusChipColor = (status: string | undefined) => {
  switch (status) {
    case "NEW":
      return "info" as const;
    case "ASSIGNED":
      return "warning" as const;
    case "IN_PROGRESS":
      return "secondary" as const;
    case "ON_HOLD":
      return "default" as const;
    case "RESOLVED":
      return "success" as const;
    case "CLOSED":
      return "default" as const;
    case "CANCELLED":
      return "error" as const;
    default:
      return "default" as const;
  }
};

type PriorityChipProps = {
  incident: Pick<Incident, "priority" | "detectedAnomalyEvents">;
  size?: "small" | "medium";
  compact?: boolean;
};

export function IncidentPriorityChip({ incident, size = "small", compact = false }: PriorityChipProps) {
  const { t } = useTranslation();
  const priority = incident.priority;
  const english = getIncidentPriorityLabel(priority);
  const label = optionLabel(t, "options.priority", priority, english);
  const hasAnomaly = hasDetectedAnomalyEvents(incident);
  const anomalyCount = hasAnomaly ? incident.detectedAnomalyEvents!.length : 0;
  const labelColor = isMediumPriorityLabel(priority) ? "#1f1f1f" : "common.white";
  const chipSx = {
    bgcolor: resolvePriorityColor(priority),
    color: labelColor,
    fontWeight: 700,
    height: compact ? 20 : undefined,
    fontSize: compact ? "0.65rem" : undefined,
    "& .MuiChip-icon": {
      color: labelColor,
      ml: hasAnomaly ? 0.75 : undefined,
    },
    "& .MuiChip-label": {
      color: labelColor,
      fontWeight: 700,
      px: compact ? 0.5 : 0.75,
    },
  };

  const chip = (
    <Chip
      label={label}
      size={size}
      icon={hasAnomaly ? renderAnomalyAlarmIcon() : undefined}
      sx={chipSx}
    />
  );

  if (hasAnomaly) {
    return (
      <Tooltip
        title={t("incidents.anomalyEventsCount", { count: anomalyCount })}
      >
        {chip}
      </Tooltip>
    );
  }

  return chip;
}

type StatusChipProps = {
  status?: string;
  size?: "small" | "medium";
  compact?: boolean;
  variant?: "filled" | "outlined";
};

export function IncidentStatusChip({
  status,
  size = "small",
  compact = false,
  variant,
}: StatusChipProps) {
  const { t } = useTranslation();
  const s = statusChipColor(status);
  const resolvedVariant = variant ?? (s === "default" ? "outlined" : "filled");

  return (
    <Chip
      label={optionLabel(t, "options.incidentStatus", status, t("common.unknown"))}
      size={size}
      color={s}
      variant={resolvedVariant}
      sx={
        compact
          ? {
              height: 20,
              fontSize: "0.65rem",
            }
          : undefined
      }
    />
  );
}
